import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { Button, Spinner, Notice } from '@wordpress/components';

const SettingsTab = () => {
    const [activeSubTab, setActiveSubTab] = useState('general');
    const [settings, setSettings] = useState({
        auto_scan: false,
        exclude_paths: '',
        duplicate_threshold: 95,
        trash_retention_days: 30,
    });
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_get_settings',
                    nonce: mtrimAdminData.nonce,
                }),
            });
            const data = await response.json();
            if (data.success && data.data) {
                setSettings({ ...settings, ...data.data });
            }
        } catch (error) {
            console.error('Failed to load settings');
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_save_settings',
                    nonce: mtrimAdminData.nonce,
                    settings: JSON.stringify(settings),
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: __('Settings saved successfully!', 'media-trim') });
            } else {
                setNotice({ type: 'error', message: data.data?.message || __('Failed to save settings', 'media-trim') });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to save settings', 'media-trim') });
        }
        setSaving(false);
    };

    const handleChange = (key, value) => {
        setSettings({ ...settings, [key]: value });
    };

    const subTabs = [
        { id: 'general', title: __('General', 'media-trim'), icon: 'admin-generic' },
        { id: 'scanning', title: __('Scanning', 'media-trim'), icon: 'search' },
        { id: 'integrations', title: __('Integrations', 'media-trim'), icon: 'networking' },
    ];

    const renderGeneralContent = () => (
        <div className="mtrim-settings-inner">
            <div className="mtrim-settings-section">
                <div className="mtrim-section-title">
                    <span className="dashicons dashicons-trash"></span>
                    <h3>{__('Trash & Cleanup', 'media-trim')}</h3>
                </div>
                
                <div className="mtrim-setting-field">
                    <div className="mtrim-label-container">
                        <label>{__('Trash Retention', 'media-trim')}</label>
                        <span className="mtrim-help-icon" title={__('How long to keep items in trash before auto-deletion.', 'media-trim')}>?</span>
                    </div>
                    <div className="mtrim-input-with-suffix">
                        <input 
                            type="number" 
                            value={settings.trash_retention_days} 
                            onChange={(e) => handleChange('trash_retention_days', e.target.value)}
                            min="1"
                            max="365"
                        />
                        <span className="mtrim-suffix">{__('Days', 'media-trim')}</span>
                    </div>
                    <p className="description">
                        {__('Items older than this will be permanently deleted from the plugin trash.', 'media-trim')}
                    </p>
                </div>
            </div>

            <div className="mtrim-settings-section">
                <div className="mtrim-section-title">
                    <span className="dashicons dashicons-no-alt"></span>
                    <h3>{__('Exclusions', 'media-trim')}</h3>
                </div>
                
                <div className="mtrim-setting-field">
                    <label>{__('Folders to Ignore', 'media-trim')}</label>
                    <textarea 
                        value={settings.exclude_paths} 
                        onChange={(e) => handleChange('exclude_paths', e.target.value)}
                        placeholder={"/wp-content/uploads/essential-addons/\n/wp-content/uploads/backups/"}
                        rows="4"
                    />
                    <p className="description">
                        {__('Add one directory path per line. These locations will be completely skipped during all scans.', 'media-trim')}
                    </p>
                </div>
            </div>
        </div>
    );

    const renderScanningContent = () => (
        <div className="mtrim-settings-inner">
            <div className="mtrim-settings-section">
                <div className="mtrim-section-title">
                    <span className="dashicons dashicons-clock"></span>
                    <h3>{__('Automated Tasks', 'media-trim')}</h3>
                </div>
                
                <div className="mtrim-setting-field checkbox-field">
                    <label className="mtrim-toggle">
                        <input 
                            type="checkbox" 
                            checked={settings.auto_scan} 
                            onChange={(e) => handleChange('auto_scan', e.target.checked)} 
                        />
                        <span className="mtrim-toggle-slider"></span>
                        <span className="mtrim-toggle-label">{__('Enable Weekly Background Scan', 'media-trim')}</span>
                    </label>
                    <p className="description">
                        {__('Perform a background scan every 7 days to keep your results updated without manual effort.', 'media-trim')}
                    </p>
                </div>
            </div>

            <div className="mtrim-settings-section">
                <div className="mtrim-section-title">
                    <span className="dashicons dashicons-images-alt2"></span>
                    <h3>{__('Duplicate Settings', 'media-trim')}</h3>
                </div>
                
                <div className="mtrim-setting-field">
                    <div className="mtrim-label-container">
                        <label>{__('Detection Sensitivity', 'media-trim')}</label>
                    </div>
                    <div className="mtrim-range-container">
                        <input 
                            type="range" 
                            value={settings.duplicate_threshold} 
                            onChange={(e) => handleChange('duplicate_threshold', e.target.value)}
                            min="70"
                            max="100"
                            step="5"
                        />
                        <span className="mtrim-range-value">{settings.duplicate_threshold}%</span>
                    </div>
                    <p className="description">
                        {__('Threshold for considering images as duplicates. 100% means bit-for-bit exact match.', 'media-trim')}
                    </p>
                </div>
            </div>
        </div>
    );

    const renderIntegrationsContent = () => (
        <div className="mtrim-settings-inner">
            <div className="mtrim-settings-section">
                <div className="mtrim-section-title">
                    <span className="dashicons dashicons-images-alt"></span>
                    <h3>{__('Pexels Integration', 'media-trim')}</h3>
                </div>
                
                <div className="mtrim-setting-field">
                    <label>{__('API Key', 'media-trim')}</label>
                    <div className="mtrim-input-icon-wrapper">
                        <span className="dashicons dashicons-lock"></span>
                        <input 
                            type="password" 
                            value={settings.pexels_api_key || ''} 
                            onChange={(e) => handleChange('pexels_api_key', e.target.value)}
                            placeholder={__('Paste your API key here...', 'media-trim')}
                        />
                    </div>
                    <p className="description">
                        {__('Required for the Stock Images tab. ', 'media-trim')}
                        <a href="https://www.pexels.com/api/" target="_blank" rel="nofollow">
                            {__('Get your free key here →', 'media-trim')}
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );



    return (
        <div className="mtrim-settings-tab">
            <div className="mtrim-settings-header">
                <h2>{__('Settings', 'media-trim')}</h2>
            </div>

            {notice && (
                <Notice 
                    status={notice.type} 
                    isDismissible={true}
                    onRemove={() => setNotice(null)}
                    style={{ marginTop: '20px' }}
                >
                    {notice.message}
                </Notice>
            )}

            <nav className="mtrim-settings-nav">
                {subTabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`mtrim-settings-nav-item ${activeSubTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveSubTab(tab.id)}
                    >
                        <span className={`dashicons dashicons-${tab.icon}`}></span>
                        {tab.title}
                    </button>
                ))}
            </nav>

            <div className="mtrim-settings-content">
                {activeSubTab === 'general' && renderGeneralContent()}
                {activeSubTab === 'scanning' && renderScanningContent()}
                {activeSubTab === 'integrations' && renderIntegrationsContent()}

                <div className="mtrim-settings-footer">
                        <Button
                            variant="primary"
                            onClick={saveSettings}
                            disabled={saving}
                            className="mtrim-save-settings-btn"
                        >
                            {saving ? <Spinner /> : __('Save Settings', 'media-trim')}
                        </Button>
                    </div>
            </div>
        </div>
    );
};

export default SettingsTab;
